import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, ArrowLeft, Check } from "lucide-react";
import { api, errMsg} from "@/lib/config";
import { useCustomer } from "@/lib/customerAuth";
import { Reveal, RevealText } from "@/lib/motion";

const AREAS = [
  { key: "irrigazione", name: "Irrigazione" },
  { key: "trattamento-acqua", name: "Trattamento Acque" },
  { key: "automazioni", name: "Automazioni" },
  { key: "elettrico", name: "Impianti Elettrici" },
  { key: "fotovoltaico", name: "Fotovoltaico" },
  { key: "antiincendio", name: "Antincendio" },
  { key: "laser", name: "Taglio Laser" },
];

export default function TicketPage() {
  const { user, authHeaders } = useCustomer();
  const nav = useNavigate();
  const [f, setF] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "", company: user?.company || "",
    area: "irrigazione", plant_name: "", title: "", description: "", priority: "normal",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [privacy, setPrivacy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!user && !privacy) { toast.error("Accetta la privacy policy per procedere"); return; }
    setSubmitting(true);
    try {
      let r;
      if (user) {
        r = await api.post("/customers/me/tickets", {
          area: f.area, plant_name: f.plant_name, title: f.title,
          description: f.description, priority: f.priority,
        }, { headers: authHeaders() });
      } else {
        if (!f.name || !f.email) throw { response: { data: { detail: "Nome ed email obbligatori" } } };
        r = await api.post("/service/tickets", f);
        await api.post("/privacy/consent-log", { kind: "form", form: "ticket", email: f.email });
      }
      setDone(r.data.ticket_id);
      toast.success("Ticket aperto");
    } catch (e) {
      toast.error(errMsg(e, "Errore invio"));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-center">
        <Check size={64} className="text-[#22c55e] mx-auto mb-8" />
        <div className="num-chip mx-auto mb-6"><span className="dot" /> Ticket · {done.slice(0, 8).toUpperCase()}</div>
        <div className="font-display text-4xl md:text-5xl mb-4">Ticket ricevuto.</div>
        <p className="text-white/60 max-w-md mx-auto mb-8">
          Un tecnico Gefion prenderà in carico la tua richiesta e ti ricontatterà secondo l'SLA del
          tuo piano. Riceverai aggiornamenti via email.
        </p>
        <div className="flex justify-center gap-3">
          {user ? (
            <button onClick={() => nav("/area")} className="btn-primary">I miei ticket</button>
          ) : (
            <Link to="/" className="btn-primary">Torna alla home</Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-24">
      <section className="py-16 border-b border-[#141414]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Reveal>
            <Link to="/assistenza" className="font-mono-tech text-white/60 hover:text-[#ff4b00]"><ArrowLeft size={12} className="inline mr-1" />Assistenza</Link>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="num-chip my-6"><span className="dot" /> {user ? `Ciao ${user.name}` : "Apertura ticket · Guest"}</div>
          </Reveal>
          <RevealText text="Apri un ticket." className="font-display text-white text-[10vw] md:text-[5.5vw] leading-[0.95]" />
          <Reveal delay={0.2}>
            <p className="text-white/60 max-w-xl mt-6">
              Descrivi il problema il più chiaramente possibile. Se hai un contratto attivo,
              <Link to="/area/login" className="text-[#ff4b00] hover:underline"> accedi </Link>
              per collegare il ticket al tuo piano SLA.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="py-16">
        <form onSubmit={submit} className="max-w-[1000px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {!user && (
            <>
              <div><label className="field-label">Nome *</label><input required data-testid="ticket-name" className="field-boxed mt-2" value={f.name} onChange={set("name")} /></div>
              <div><label className="field-label">Email *</label><input required type="email" data-testid="ticket-email" className="field-boxed mt-2" value={f.email} onChange={set("email")} /></div>
              <div><label className="field-label">Telefono</label><input data-testid="ticket-phone" className="field-boxed mt-2" value={f.phone} onChange={set("phone")} /></div>
              <div><label className="field-label">Azienda</label><input data-testid="ticket-company" className="field-boxed mt-2" value={f.company} onChange={set("company")} /></div>
            </>
          )}
          <div>
            <label className="field-label">Divisione impianto *</label>
            <select data-testid="ticket-area" className="field-boxed mt-2" value={f.area} onChange={set("area")}>
              {AREAS.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
            </select>
          </div>
          <div><label className="field-label">Nome/indirizzo impianto (opz.)</label><input data-testid="ticket-plant" className="field-boxed mt-2" value={f.plant_name} onChange={set("plant_name")} placeholder="es. Sede via Roma, Serra 3" /></div>
          <div><label className="field-label">Titolo *</label><input required data-testid="ticket-title" className="field-boxed mt-2" value={f.title} onChange={set("title")} placeholder="es. Pompa fuori servizio zona nord" /></div>
          <div>
            <label className="field-label">Priorità</label>
            <select data-testid="ticket-priority" className="field-boxed mt-2" value={f.priority} onChange={set("priority")}>
              <option value="low">Bassa</option>
              <option value="normal">Normale</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <div className="md:col-span-2"><label className="field-label">Descrizione dettagliata *</label><textarea required data-testid="ticket-description" rows={6} className="field-boxed mt-2 resize-none" value={f.description} onChange={set("description")} placeholder="Cosa è successo? Da quando? Sintomi..." /></div>
          {!user && (
            <label className="md:col-span-2 flex items-start gap-3 text-white/70 text-sm cursor-pointer">
              <input type="checkbox" required checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} data-testid="ticket-privacy" className="mt-1 accent-[#ff4b00]" />
              <span>Ho letto e accetto la <Link to="/privacy" className="text-[#ff4b00] hover:underline">Privacy Policy</Link>. Autorizzo il trattamento dei miei dati per la gestione del ticket. *</span>
            </label>
          )}
          <div className="md:col-span-2 flex items-center justify-between pt-4">
            <div className="font-mono-tech text-[10px] text-white/40 max-w-xs">L'SLA di risposta dipende dal tuo piano di assistenza attivo.</div>
            <button disabled={submitting} data-testid="ticket-submit" className="btn-primary">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Invia ticket <Send size={14} /></>}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
